import {
  ActionIcon, Button, useMantineColorScheme,
} from '@mantine/core';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight, Menu2, MoonStars, Sun, X,
} from 'tabler-icons-react';

import s from './Header.module.scss';

type HeaderProps = {
  variant?: 'transparent' | 'solid';
};

const navLinks = [
  { label: 'How it works', to: '/how-it-works' },
  { label: 'Features', to: '/features' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Integrations', to: '/integrations' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

function Header({ variant = 'solid' }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const colorSchemeCtx = useMantineColorScheme();
  const { colorScheme } = colorSchemeCtx;
  const handleToggle = () => colorSchemeCtx.toggleColorScheme();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (variant !== 'transparent') {
      return undefined;
    }
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [variant]);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const headerClass = [
    s.header,
    s[variant],
    scrolled ? s.scrolled : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <nav className={headerClass}>
        <div className={s.inner}>
          <Link to="/" className={s.logo}>
            FlowBoard
          </Link>

          {/* Desktop nav */}
          <div className={s.desktopNav}>
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={
                  `${s.navLink} `
                  + `${location.pathname === link.to ? s.active : ''}`
                }
              >
                {link.label}
              </Link>
            ))}

            <ActionIcon
              variant="subtle"
              color="gray"
              radius="xl"
              size="lg"
              onClick={handleToggle}
              sx={{ color: 'rgba(255,255,255,0.85)' }}
            >
              {colorScheme === 'dark'
                ? <Sun size={18} />
                : <MoonStars size={18} />}
            </ActionIcon>

            <Button
              variant="subtle"
              size="sm"
              onClick={() => navigate('/login')}
              sx={{
                color: 'white',
                '&:hover': {
                  background: 'rgba(255,255,255,0.08)',
                },
              }}
            >
              Sign in
            </Button>
            <Button
              size="sm"
              radius="xl"
              variant="gradient"
              gradient={{ from: 'indigo', to: 'violet' }}
              onClick={() => navigate('/request-access')}
              rightIcon={<ArrowRight size={16} />}
            >
              Request access
            </Button>
          </div>

          {/* Hamburger (mobile) */}
          <div className="tw-flex tw-items-center tw-gap-2 md:tw-hidden">
            <ActionIcon
              variant="subtle"
              color="gray"
              radius="xl"
              size="lg"
              onClick={handleToggle}
              sx={{ color: 'rgba(255,255,255,0.85)' }}
            >
              {colorScheme === 'dark'
                ? <Sun size={18} />
                : <MoonStars size={18} />}
            </ActionIcon>
            <button
              type="button"
              className={s.hamburger}
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
            >
              <Menu2 size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      <div
        className={
          `${s.mobileOverlay} ${drawerOpen ? s.open : ''}`
        }
        onClick={closeDrawer}
        onKeyDown={(e) => e.key === 'Escape' && closeDrawer()}
        role="button"
        tabIndex={-1}
        aria-label="Close menu"
      />

      {/* Mobile drawer */}
      <div
        className={
          `${s.mobileDrawer} ${drawerOpen ? s.open : ''}`
        }
      >
        <div className={s.drawerHeader}>
          <span className={s.drawerLogo}>FlowBoard</span>
          <button
            type="button"
            className={s.closeBtn}
            onClick={closeDrawer}
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>

        <div className={s.drawerBody}>
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={
                `${s.mobileLink} `
                + `${location.pathname === link.to
                  ? s.active : ''}`
              }
              onClick={closeDrawer}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className={s.drawerDivider} />

        <div className={s.drawerActions}>
          <Button
            fullWidth
            variant="subtle"
            size="md"
            onClick={() => {
              navigate('/login');
              closeDrawer();
            }}
            sx={{
              color: 'white',
              '&:hover': {
                background: 'rgba(255,255,255,0.08)',
              },
            }}
          >
            Sign in
          </Button>
          <Button
            fullWidth
            size="md"
            radius="xl"
            variant="gradient"
            gradient={{ from: 'indigo', to: 'violet' }}
            onClick={() => {
              navigate('/request-access');
              closeDrawer();
            }}
            rightIcon={<ArrowRight size={16} />}
          >
            Request access
          </Button>
        </div>
      </div>
    </>
  );
}

export default Header;
